import { createContext, useCallback, useContext, useEffect, useState } from "react";

import { api, clearTokens, loadTokens, saveTokens, setUnauthorizedHandler } from "@/lib/api";
import { queryClient } from "@/lib/query";

const Ctx = createContext(null);

// user: null = checking, false = logged out, object = logged in (same as mobile)
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  const logout = useCallback(async () => {
    clearTokens();
    queryClient.clear();
    setUser(false);
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      queryClient.clear();
      setUser(false);
    });
    (async () => {
      if (!loadTokens()) return setUser(false);
      try {
        setUser(await api("/auth/me"));
      } catch {
        clearTokens();
        setUser(false);
      }
    })();
  }, []);

  const login = useCallback(async (email, password, role) => {
    const data = await api(role === "admin" ? "/admin/auth/login" : "/auth/login", {
      method: "POST",
      body: { email, password },
    });
    saveTokens(data.access_token, data.refresh_token);
    queryClient.clear();
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (body) => {
    const data = await api("/auth/register", { method: "POST", body });
    saveTokens(data.access_token, data.refresh_token);
    queryClient.clear();
    setUser(data.user);
    return data.user;
  }, []);

  const refresh = useCallback(async () => {
    setUser(await api("/auth/me"));
  }, []);

  return <Ctx.Provider value={{ user, login, register, logout, refresh, setUser }}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const c = useContext(Ctx);
  if (!c) throw new Error("useAuth outside AuthProvider");
  return c;
}
